import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import Dashboard from './Dashboard';
import './GroceryStockDashboard.css';

const BASE_URL = process.env.REACT_APP_API_URL || 'https://vakra.co.in';

const emptyDashboard = {
  summary: {
    totalProducts: 0,
    outOfStockCount: 0,
    lowStockCount: 0,
    stockValue: 0,
    todaySales: 0,
    totalCustomers: 0,
    pendingCustomers: 0,
    totalPendingAmount: 0,
    totalReceivedAmount: 0,
    todayReceivedAmount: 0,
    reportReceivedAmount: 0,
    manualReceivedAmount: 0,
    directPaidReceivedAmount: 0,
    todayManualReceivedAmount: 0,
    todayDirectPaidAmount: 0,
    recentBillsCount: 0,
  },
  dueAging: {
    days0to7: 0,
    days8to30: 0,
    days30plus: 0,
  },
  dailySales: [],
  lowStockItems: [],
  topSellingItems: [],
  productsTable: [],
  customers: [],
  recentLedger: [],
};

const REPORT_OPTIONS = [
  { value: 'today', label: 'Today', shortLabel: 'Today', days: 1 },
  { value: '7', label: 'Last 7 Days', shortLabel: '7 Days', days: 7 },
  { value: '10', label: 'Last 10 Days', shortLabel: '10 Days', days: 10 },
  { value: '20', label: 'Last 20 Days', shortLabel: '20 Days', days: 20 },
  { value: '30', label: 'Last 30 Days', shortLabel: '30 Days', days: 30 },
  { value: '90', label: 'Last 90 Days', shortLabel: '90 Days', days: 90 },
  { value: 'custom', label: 'Custom Date', shortLabel: 'Custom', days: null },
];

const GroceryStockDashboard = () => {
  const [activeSection, setActiveSection] = useState('khata');
  const [khataView, setKhataView] = useState('home');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [groceryMode, setGroceryMode] = useState(
    localStorage.getItem('groceryMode') || localStorage.getItem('grocery_mode') || 'retail'
  );

  const [reportPreset, setReportPreset] = useState('today');
  const [reportStartDate, setReportStartDate] = useState(() =>
    new Date().toISOString().slice(0, 10)
  );
  const [reportEndDate, setReportEndDate] = useState(() =>
    new Date().toISOString().slice(0, 10)
  );
  const [showReceivedFilter, setShowReceivedFilter] = useState(false);

  const [dashboardData, setDashboardData] = useState(emptyDashboard);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const [stockSearch, setStockSearch] = useState('');
  const [stockTab, setStockTab] = useState('all');

  const [customerSearch, setCustomerSearch] = useState('');
  const [customerFilter, setCustomerFilter] = useState('all');

  const [customerInsight, setCustomerInsight] = useState({
    orders: [],
    purchasedProducts: [],
    paymentTimeline: [],
    totalBills: 0,
    totalOrders: 0,
    totalPurchasedQty: 0,
  });

  const [paymentBox, setPaymentBox] = useState({
    open: false,
    type: 'partial',
    bill: null,
    amount: '',
    paymentMode: 'cash',
    note: '',
    referenceNo: '',
  });

  const [savingPayment, setSavingPayment] = useState(false);
  const customerListRef = useRef(null);
  const customerDetailRef = useRef(null);

  const getToken = () => localStorage.getItem('token') || '';

  const authHeaders = () => ({
    Authorization: `Bearer ${getToken()}`,
  });

  const formatCurrency = (value) =>
    `₹ ${Number(value || 0).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const formatShortCurrency = (value) =>
    `₹ ${Number(value || 0).toLocaleString('en-IN', {
      maximumFractionDigits: 0,
    })}`;

  const formatDate = (value) => {
    if (!value || value === '-') return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatDateTime = (value) => {
    if (!value || value === '-') return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getPresetDays = (preset) => {
    const found = REPORT_OPTIONS.find((item) => item.value === preset);
    if (found && found.days) return found.days;
    return 1;
  };

  const getReportParams = () => {
    if (reportPreset === 'custom') {
      return {
        startDate: reportStartDate,
        endDate: reportEndDate,
      };
    }

    return {
      reportDays: getPresetDays(reportPreset),
    };
  };

  const getReportLabel = () => {
    if (reportPreset === 'custom') {
      if (reportStartDate && reportEndDate) {
        return `${formatDate(reportStartDate)} - ${formatDate(reportEndDate)}`;
      }
      return 'Custom Date';
    }

    const found = REPORT_OPTIONS.find((item) => item.value === reportPreset);
    return found?.label || 'Today';
  };

  const getReportShortLabel = () => {
    if (reportPreset === 'custom') return 'Custom';
    const found = REPORT_OPTIONS.find((item) => item.value === reportPreset);
    return found?.shortLabel || 'Today';
  };

  const changeReportPreset = (nextPreset) => {
    setReportPreset(nextPreset);
    if (nextPreset !== 'custom') {
      setShowReceivedFilter(false);
    }
  };

  const applyCustomDateFilter = () => {
    if (!reportStartDate || !reportEndDate) {
      setMessage('Custom report ke liye From aur To date select karein.');
      return;
    }

    if (new Date(reportStartDate) > new Date(reportEndDate)) {
      setMessage('From date, To date se badi nahi ho sakti.');
      return;
    }

    setReportPreset('custom');
    setShowReceivedFilter(false);
    fetchDashboard({ silent: false });
  };

  const reportReceivedAmount = Number(
    dashboardData.summary.reportReceivedAmount ?? dashboardData.summary.todayReceivedAmount ?? 0
  );

  const scrollToRef = (ref) => {
    setTimeout(() => {
      if (ref?.current) {
        ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 80);
  };

  const isRealCustomerName = (value) => {
    const name = String(value || '').trim();

    if (!name) return false;

    const lower = name.toLowerCase();

    if (
      lower === 'customer' ||
      lower === 'null' ||
      lower === 'undefined' ||
      lower === 'na' ||
      lower === 'n/a' ||
      lower === '-' ||
      lower === 'unknown' ||
      lower === 'guest' ||
      lower.startsWith('customer ')
    ) {
      return false;
    }

    if (/^\d+$/.test(name)) return false;

    return true;
  };

  const getCustomerName = (customer) => {
    if (!customer) return 'Customer';

    const possibleNames = [
      customer.customer_name,
      customer.name,
      customer.customerName,
      customer.latest_customer_name,
      customer.latest_bill_customer_name,
      customer.bill_customer_name,
      customer.order_customer_name,
      customer.delivery_customer_name,
      customer.saved_customer_name,
      customer.customer_full_name,
      customer.full_name,
      customer?.pending_bills?.find?.((bill) => isRealCustomerName(bill.customer_name))
        ?.customer_name,
      customer?.pending_bills?.find?.((bill) => isRealCustomerName(bill.delivery_customer_name))
        ?.delivery_customer_name,
      customer?.ledger?.find?.((item) => isRealCustomerName(item.customer_name))?.customer_name,
      customer?.ledger?.find?.((item) => isRealCustomerName(item.delivery_customer_name))
        ?.delivery_customer_name,
    ];

    const found = possibleNames.find((name) => isRealCustomerName(name));

    if (found) return String(found).trim();

    const mobile = String(customer.customer_mobile || customer.mobile || '').trim();
    if (mobile) return `Customer ${mobile.slice(-4)}`;

    return 'Customer';
  };

  const getCustomerInitial = (customerOrName, mobile = '') => {
    const name =
      typeof customerOrName === 'object'
        ? getCustomerName(customerOrName)
        : String(customerOrName || mobile || 'C');

    return String(name || mobile || 'C').trim().slice(0, 1).toUpperCase();
  };

  const getCustomerMobile = (customer) =>
    String(customer?.customer_mobile || customer?.mobile || '').trim();

  const getCustomerDue = (customer) => Number(customer?.pending_amount || customer?.balance_due || 0);

  const getCustomerPaid = (customer) => Number(customer?.paid || customer?.paid_amount || 0);

  const getCustomerStatus = (customer) => {
    const due = getCustomerDue(customer);
    const paid = getCustomerPaid(customer);
    if (due <= 0) return 'paid';
    if (paid > 0) return 'partial';
    return 'due';
  };

  const getStatusLabel = (status) => {
    if (status === 'paid') return 'Paid';
    if (status === 'partial') return 'Partial';
    return 'Due';
  };

  const normalizeCustomers = (customers = []) => {
    return customers.map((customer) => {
      const finalName = getCustomerName(customer);
      const finalMobile = getCustomerMobile(customer);

      return {
        ...customer,
        customer_name: finalName,
        display_name: finalName,
        customer_mobile: finalMobile,
      };
    });
  };

  const fetchDashboard = async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      setMessage('');

      const res = await axios.get(`${BASE_URL}/api/grocery-stock/dashboard`, {
        headers: authHeaders(),
        params: {
          groceryMode,
          mode: groceryMode,
          ...getReportParams(),
        },
      });

      if (!res?.data?.success) {
        setDashboardData(emptyDashboard);
        setMessage(res?.data?.message || 'Dashboard data nahi mila.');
        return;
      }

      const rawCustomers = Array.isArray(res.data.customers) ? res.data.customers : [];
      const customers = normalizeCustomers(rawCustomers);

      const nextData = {
        summary: {
          totalProducts: Number(res.data.summary?.totalProducts || 0),
          outOfStockCount: Number(res.data.summary?.outOfStockCount || 0),
          lowStockCount: Number(res.data.summary?.lowStockCount || 0),
          stockValue: Number(res.data.summary?.stockValue || 0),
          todaySales: Number(res.data.summary?.todaySales || 0),
          totalCustomers: Number(res.data.summary?.totalCustomers || customers.length || 0),
          pendingCustomers: Number(res.data.summary?.pendingCustomers || 0),
          totalPendingAmount: Number(res.data.summary?.totalPendingAmount || 0),
          totalReceivedAmount: Number(res.data.summary?.totalReceivedAmount || 0),
          todayReceivedAmount: Number(res.data.summary?.todayReceivedAmount || 0),
          reportReceivedAmount: Number(
            res.data.summary?.reportReceivedAmount ?? res.data.summary?.todayReceivedAmount ?? 0
          ),
          manualReceivedAmount: Number(res.data.summary?.manualReceivedAmount || 0),
          directPaidReceivedAmount: Number(res.data.summary?.directPaidReceivedAmount || 0),
          todayManualReceivedAmount: Number(res.data.summary?.todayManualReceivedAmount || 0),
          todayDirectPaidAmount: Number(res.data.summary?.todayDirectPaidAmount || 0),
          recentBillsCount: Number(res.data.summary?.recentBillsCount || 0),
        },
        dueAging: {
          days0to7: Number(res.data.dueAging?.days0to7 || 0),
          days8to30: Number(res.data.dueAging?.days8to30 || 0),
          days30plus: Number(res.data.dueAging?.days30plus || 0),
        },
        dailySales: Array.isArray(res.data.dailySales) ? res.data.dailySales : [],
        lowStockItems: Array.isArray(res.data.lowStockItems) ? res.data.lowStockItems : [],
        topSellingItems: Array.isArray(res.data.topSellingItems) ? res.data.topSellingItems : [],
        productsTable: Array.isArray(res.data.productsTable) ? res.data.productsTable : [],
        customers,
        recentLedger: Array.isArray(res.data.recentLedger) ? res.data.recentLedger : [],
      };

      setDashboardData(nextData);

      if (customers.length > 0) {
        const previous =
          selectedCustomer &&
          customers.find(
            (c) =>
              String(getCustomerMobile(c)) === String(getCustomerMobile(selectedCustomer)) ||
              String(c.id || '') === String(selectedCustomer.id || '')
          );

        const firstDue = customers.find((c) => getCustomerDue(c) > 0);
        setSelectedCustomer(previous || firstDue || customers[0]);
      } else {
        setSelectedCustomer(null);
      }
    } catch (err) {
      console.error('fetchDashboard error:', err);
      setMessage(err?.response?.data?.message || err?.message || 'Dashboard load nahi hua.');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const fetchCustomerInsight = async (customer) => {
    const mobile = getCustomerMobile(customer);

    if (!mobile) {
      setCustomerInsight({
        orders: [],
        purchasedProducts: [],
        paymentTimeline: [],
        totalBills: 0,
        totalOrders: 0,
        totalPurchasedQty: 0,
      });
      return;
    }

    try {
      const res = await axios.get(
        `${BASE_URL}/api/grocery-stock/customer-history/${encodeURIComponent(mobile)}`,
        {
          headers: authHeaders(),
          params: {
            groceryMode,
            mode: groceryMode,
            days: 365,
          },
        }
      );

      if (res?.data?.success) {
        setCustomerInsight({
          orders: Array.isArray(res.data.orders) ? res.data.orders : [],
          purchasedProducts: Array.isArray(res.data.purchasedProducts)
            ? res.data.purchasedProducts
            : [],
          paymentTimeline: Array.isArray(res.data.paymentTimeline)
            ? res.data.paymentTimeline
            : [],
          totalBills: Number(res.data.totalBills || 0),
          totalOrders: Number(res.data.totalOrders || 0),
          totalPurchasedQty: Number(res.data.totalPurchasedQty || 0),
        });
      }
    } catch (err) {
      console.error('fetchCustomerInsight error:', err);
      setCustomerInsight({
        orders: [],
        purchasedProducts: [],
        paymentTimeline: [],
        totalBills: 0,
        totalOrders: 0,
        totalPurchasedQty: 0,
      });
    }
  };

  useEffect(() => {
    localStorage.setItem('groceryMode', groceryMode);
    fetchDashboard();
  }, [groceryMode, reportPreset]);

  useEffect(() => {
    if (reportPreset === 'custom') {
      fetchDashboard();
    }
  }, [reportStartDate, reportEndDate]);

  useEffect(() => {
    if (selectedCustomer) {
      fetchCustomerInsight(selectedCustomer);
    }
  }, [selectedCustomer?.customer_mobile, groceryMode]);

  const stockCounts = useMemo(() => {
    const rows = dashboardData.productsTable || [];
    return {
      all: rows.length,
      in_stock: rows.filter((x) => x.stock_status === 'in_stock').length,
      low_stock: rows.filter((x) => x.stock_status === 'low_stock').length,
      out_of_stock: rows.filter((x) => x.stock_status === 'out_of_stock').length,
    };
  }, [dashboardData.productsTable]);

  const filteredProducts = useMemo(() => {
    const q = stockSearch.trim().toLowerCase();

    return (dashboardData.productsTable || []).filter((item) => {
      const text = [
        item.product_name,
        item.sku,
        item.category,
        item.brand,
        item.unit,
        item.stock_status,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      const searchOk = !q || text.includes(q);
      const tabOk = stockTab === 'all' || item.stock_status === stockTab;
      return searchOk && tabOk;
    });
  }, [dashboardData.productsTable, stockSearch, stockTab]);

  const filteredCustomers = useMemo(() => {
    const q = customerSearch.trim().toLowerCase();

    let rows = [...(dashboardData.customers || [])];

    if (q) {
      rows = rows.filter((customer) => {
        const text = [
          getCustomerName(customer),
          customer.customer_name,
          customer.name,
          customer.display_name,
          customer.customer_mobile,
          customer.mobile,
          customer.customer_type,
          customer.address,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        return text.includes(q);
      });
    }

    if (customerFilter === 'due') {
      rows = rows.filter((c) => getCustomerStatus(c) === 'due');
    } else if (customerFilter === 'partial') {
      rows = rows.filter((c) => getCustomerStatus(c) === 'partial');
    } else if (customerFilter === 'paid') {
      rows = rows.filter((c) => getCustomerStatus(c) === 'paid');
    }

    return rows.sort((a, b) => {
      const dueDiff = getCustomerDue(b) - getCustomerDue(a);
      if (dueDiff !== 0) return dueDiff;
      return getCustomerName(a).localeCompare(getCustomerName(b));
    });
  }, [dashboardData.customers, customerSearch, customerFilter]);

  const selectedBills = useMemo(() => {
    const bills = Array.isArray(selectedCustomer?.pending_bills)
      ? selectedCustomer.pending_bills
      : [];
    return bills.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
  }, [selectedCustomer]);

  const totalSelectedDue = getCustomerDue(selectedCustomer);

  const openCustomers = (filter = 'all') => {
    setActiveSection('khata');
    setKhataView('customers');
    setCustomerFilter(filter);
    scrollToRef(customerListRef);
  };

  const openCustomerDetail = (customer) => {
    setSelectedCustomer({
      ...customer,
      customer_name: getCustomerName(customer),
      customer_mobile: getCustomerMobile(customer),
    });
    setKhataView('detail');
    scrollToRef(customerDetailRef);
  };

  const openPaymentBox = ({ type = 'partial', bill = null, amount = '' }) => {
    setPaymentBox({
      open: true,
      type,
      bill,
      amount,
      paymentMode: 'cash',
      note: '',
      referenceNo: '',
    });
  };

  const closePaymentBox = () => {
    setPaymentBox({
      open: false,
      type: 'partial',
      bill: null,
      amount: '',
      paymentMode: 'cash',
      note: '',
      referenceNo: '',
    });
  };

  const submitPayment = async () => {
    const amount = Number(paymentBox.amount || 0);

    if (!getCustomerMobile(selectedCustomer)) {
      setMessage('Customer select nahi hua.');
      return;
    }

    if (!amount || amount <= 0) {
      setMessage('Payment amount sahi डालें.');
      return;
    }

    if (amount > totalSelectedDue && totalSelectedDue > 0) {
      const yes = window.confirm(
        `Amount due se zyada hai. Due ${formatCurrency(
          totalSelectedDue
        )} hai. Kya phir bhi save karna hai?`
      );
      if (!yes) return;
    }

    try {
      setSavingPayment(true);
      setMessage('');

      if (paymentBox.bill?.bill_id) {
        await axios.post(
          `${BASE_URL}/api/grocery-stock/settle-bill`,
          {
            billId: paymentBox.bill.bill_id,
            amount,
            paymentMode: paymentBox.paymentMode,
            note: paymentBox.note,
            referenceNo: paymentBox.referenceNo,
          },
          { headers: authHeaders() }
        );
      } else {
        await axios.post(
          `${BASE_URL}/api/grocery-stock/add-payment`,
          {
            customerId: selectedCustomer.id,
            customerName: getCustomerName(selectedCustomer),
            customerMobile: getCustomerMobile(selectedCustomer),
            customerType: selectedCustomer.customer_type || groceryMode,
            groceryMode,
            amount,
            paymentMode: paymentBox.paymentMode,
            note:
              paymentBox.note ||
              (paymentBox.type === 'full'
                ? 'Full khata payment received'
                : 'Partial khata payment received'),
            referenceNo: paymentBox.referenceNo,
          },
          { headers: authHeaders() }
        );
      }

      setMessage('Payment save ho gaya. Hisab update ho raha hai.');
      closePaymentBox();

      await fetchDashboard({ silent: false });

      if (selectedCustomer) {
        await fetchCustomerInsight(selectedCustomer);
      }
    } catch (err) {
      console.error('submitPayment error:', err);
      setMessage(err?.response?.data?.message || err?.message || 'Payment save nahi hua.');
    } finally {
      setSavingPayment(false);
    }
  };

  const sendWhatsAppReminder = (customer) => {
    const mobile = getCustomerMobile(customer);

    if (!mobile) {
      setMessage('Customer mobile number missing hai.');
      return;
    }

    const cleanMobile = String(mobile).replace(/\D/g, '');
    const due = formatCurrency(getCustomerDue(customer));
    const name = getCustomerName(customer);

    const text = `Namaste ${name} ji, aapka grocery khata ${due} baki hai. Kripya payment kar dein.`;
    const url = `https://wa.me/91${cleanMobile.slice(-10)}?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const printCustomerReceipt = (customer) => {
    if (!customer) return;

    const due = formatCurrency(getCustomerDue(customer));
    const paid = formatCurrency(getCustomerPaid(customer));
    const total = formatCurrency(customer.total_purchase || 0);
    const name = getCustomerName(customer);
    const mobile = getCustomerMobile(customer);

    const html = `
      <html>
        <head>
          <title>Khata Receipt</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              padding: 12px;
              color: #111;
            }
            .receipt {
              width: 280px;
              margin: 0 auto;
            }
            h2 {
              text-align: center;
              margin: 0 0 8px;
              font-size: 18px;
            }
            .line {
              border-top: 1px dashed #333;
              margin: 8px 0;
            }
            .row {
              display: flex;
              justify-content: space-between;
              font-size: 13px;
              margin: 6px 0;
              gap: 10px;
            }
            .row strong {
              text-align: right;
            }
            .big {
              font-size: 18px;
              font-weight: 700;
            }
            @media print {
              @page {
                size: 80mm auto;
                margin: 4mm;
              }
            }
          </style>
        </head>
        <body>
          <div class="receipt">
            <h2>Khata Receipt</h2>
            <div class="line"></div>
            <div class="row"><span>Name</span><strong>${name}</strong></div>
            <div class="row"><span>Mobile</span><strong>${mobile || '-'}</strong></div>
            <div class="row"><span>Date</span><strong>${new Date().toLocaleString('en-IN')}</strong></div>
            <div class="line"></div>
            <div class="row"><span>Total Purchase</span><strong>${total}</strong></div>
            <div class="row"><span>Total Paid</span><strong>${paid}</strong></div>
            <div class="row big"><span>Balance Due</span><strong>${due}</strong></div>
            <div class="line"></div>
            <p style="text-align:center;font-size:12px;">Thank you</p>
          </div>
          <script>
            window.onload = function () {
              window.print();
              setTimeout(function () { window.close(); }, 500);
            };
          </script>
        </body>
      </html>
    `;

    const win = window.open('', '_blank', 'width=360,height=600');
    if (win) {
      win.document.write(html);
      win.document.close();
    }
  };

  const renderReceivedFilterPopover = () => (
    <div className="gsd-received-filter-popover">
      <div className="gsd-received-filter-head">
        <strong>Report Filter</strong>
        <span>{getReportLabel()}</span>
      </div>

      <div className="gsd-received-filter-list">
        {REPORT_OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            className={reportPreset === option.value ? 'active' : ''}
            onClick={(e) => {
              e.stopPropagation();
              changeReportPreset(option.value);
            }}
          >
            {option.label}
          </button>
        ))}
      </div>

      {reportPreset === 'custom' ? (
        <div className="gsd-received-custom-box" onClick={(e) => e.stopPropagation()}>
          <label>
            <span>From</span>
            <input
              type="date"
              value={reportStartDate}
              onChange={(e) => setReportStartDate(e.target.value)}
            />
          </label>

          <label>
            <span>To</span>
            <input
              type="date"
              value={reportEndDate}
              onChange={(e) => setReportEndDate(e.target.value)}
            />
          </label>

          <button type="button" onClick={applyCustomDateFilter}>
            Apply
          </button>
        </div>
      ) : null}
    </div>
  );

  const renderStockSection = () => (
    <>
      <div className="gsd-summary-grid">
        <div className="gsd-summary-card">
          <div className="gsd-summary-icon blue">📦</div>
          <div>
            <div className="gsd-summary-title">Total Products</div>
            <div className="gsd-summary-value">{dashboardData.summary.totalProducts}</div>
          </div>
        </div>

        <div className="gsd-summary-card">
          <div className="gsd-summary-icon green">✅</div>
          <div>
            <div className="gsd-summary-title">In Stock</div>
            <div className="gsd-summary-value success">{stockCounts.in_stock}</div>
          </div>
        </div>

        <div className="gsd-summary-card">
          <div className="gsd-summary-icon yellow">⚠️</div>
          <div>
            <div className="gsd-summary-title">Low Stock</div>
            <div className="gsd-summary-value warning">{dashboardData.summary.lowStockCount}</div>
          </div>
        </div>

        <div className="gsd-summary-card">
          <div className="gsd-summary-icon red">⛔</div>
          <div>
            <div className="gsd-summary-title">Out of Stock</div>
            <div className="gsd-summary-value danger">{dashboardData.summary.outOfStockCount}</div>
          </div>
        </div>
      </div>

      <div className="gsd-panel-card">
        <div className="gsd-toolbar-top">
          <div className="gsd-search-wrap">
            <input
              type="text"
              className="gsd-search-input"
              placeholder="Product search karein..."
              value={stockSearch}
              onChange={(e) => setStockSearch(e.target.value)}
            />
          </div>

          <div className="gsd-toolbar-actions">
            <select
              className="gsd-filter-select"
              value={groceryMode}
              onChange={(e) => setGroceryMode(e.target.value)}
            >
              <option value="retail">Retail</option>
              <option value="wholesale">Wholesale</option>
            </select>

            <button type="button" className="gsd-icon-btn" onClick={() => fetchDashboard()}>
              Refresh
            </button>
          </div>
        </div>

        <div className="gsd-stock-filter-row">
          {[
            ['all', 'All Stock'],
            ['in_stock', 'In Stock'],
            ['low_stock', 'Low Stock'],
            ['out_of_stock', 'Out of Stock'],
          ].map(([value, label]) => (
            <button
              key={value}
              type="button"
              className={`gsd-tab-btn ${stockTab === value ? 'active' : ''}`}
              onClick={() => setStockTab(value)}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="gsd-table-wrap">
          <table className="gsd-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>SKU / Category</th>
                <th>Stock</th>
                <th>Stock Value</th>
                <th>Sold</th>
                <th>Sales Value</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="gsd-empty-cell">
                    Loading...
                  </td>
                </tr>
              ) : filteredProducts.length === 0 ? (
                <tr>
                  <td colSpan="6" className="gsd-empty-cell">
                    Product nahi mila.
                  </td>
                </tr>
              ) : (
                filteredProducts.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <div className="gsd-product-name">{item.product_name}</div>
                      <div className="gsd-product-unit">{item.unit || '-'}</div>
                    </td>
                    <td>{item.sku || item.category || '-'}</td>
                    <td>
                      <span
                        className={`gsd-stock-pill ${
                          item.stock_status === 'out_of_stock'
                            ? 'danger'
                            : item.stock_status === 'low_stock'
                            ? 'warning'
                            : 'success'
                        }`}
                      >
                        {item.stock_quantity}
                      </span>
                    </td>
                    <td>{formatCurrency(item.stock_value)}</td>
                    <td>{item.sold_quantity || 0}</td>
                    <td>{formatCurrency(item.sales_value)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );

  const renderKhataHome = () => {
    const topDueCustomers = [...(dashboardData.customers || [])]
      .filter((c) => getCustomerDue(c) > 0)
      .slice(0, 6);

    return (
      <>
        <div className="gsd-khata-home-grid">
          <button type="button" className="gsd-khata-stat blue" onClick={() => openCustomers('due')}>
            <div className="gsd-stat-icon">₹</div>
            <div>
              <span>Total Due</span>
              <strong>{formatShortCurrency(dashboardData.summary.totalPendingAmount)}</strong>
              <small>{dashboardData.summary.pendingCustomers} customers</small>
            </div>
          </button>

          <div className="gsd-khata-stat green gsd-received-stat-card">
            <button
              type="button"
              className="gsd-received-card-main"
              onClick={() => openCustomers('paid')}
            >
              <div className="gsd-stat-icon">↓</div>

              <div className="gsd-received-card-content">
                <div className="gsd-received-title-row">
                  <span>
                    {reportPreset === 'today' ? 'Today Received' : `${getReportShortLabel()} Received`}
                  </span>
                </div>

                <strong>{formatShortCurrency(reportReceivedAmount)}</strong>

                <small>
                  Direct: {formatShortCurrency(dashboardData.summary.directPaidReceivedAmount)} · Manual:{' '}
                  {formatShortCurrency(dashboardData.summary.manualReceivedAmount)}
                </small>
              </div>
            </button>

            <button
              type="button"
              className={`gsd-received-filter-icon ${showReceivedFilter ? 'active' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                setShowReceivedFilter((prev) => !prev);
              }}
              title="Report Filter"
            >
              ⚙️
            </button>

            {showReceivedFilter ? renderReceivedFilterPopover() : null}
          </div>

          <button type="button" className="gsd-khata-stat yellow" onClick={() => openCustomers('due')}>
            <div className="gsd-stat-icon">👥</div>
            <div>
              <span>Due Customers</span>
              <strong>{dashboardData.summary.pendingCustomers}</strong>
              <small>Baki wale customer</small>
            </div>
          </button>

          <button type="button" className="gsd-khata-stat purple" onClick={() => openCustomers('all')}>
            <div className="gsd-stat-icon">🧾</div>
            <div>
              <span>Recent Bills</span>
              <strong>{dashboardData.summary.recentBillsCount}</strong>
              <small>{getReportLabel()}</small>
            </div>
          </button>
        </div>

        <div className="gsd-big-action-grid">
          <button type="button" className="gsd-big-action primary" onClick={() => openCustomers('due')}>
            <span>💰</span>
            <div>
              <strong>Paisa Lena Hai</strong>
              <small>Due customer list kholo</small>
            </div>
          </button>

          <button
            type="button"
            className="gsd-big-action success"
            onClick={() => {
              openCustomers('due');
              if (selectedCustomer) {
                setKhataView('detail');
              }
            }}
          >
            <span>✅</span>
            <div>
              <strong>Paisa Mila</strong>
              <small>Payment save karo</small>
            </div>
          </button>

          <button type="button" className="gsd-big-action purple" onClick={() => openCustomers('all')}>
            <span>👤</span>
            <div>
              <strong>Customer Hisab</strong>
              <small>Customer ka pura khata</small>
            </div>
          </button>
        </div>

        <div className="gsd-khata-desktop-layout">
          <div className="gsd-panel-card">
            <div className="gsd-card-header-row">
              <div>
                <div className="gsd-section-title">Sabse Pehle Paisa Lena Hai</div>
                <div className="gsd-muted-text">Due wale customer upar dikh rahe hain.</div>
              </div>
              <button type="button" className="gsd-icon-btn active" onClick={() => openCustomers('due')}>
                View All
              </button>
            </div>

            <div className="gsd-customer-card-list">
              {loading ? (
                <div className="gsd-empty-box">Loading...</div>
              ) : topDueCustomers.length === 0 ? (
                <div className="gsd-empty-box">Abhi koi due customer nahi hai.</div>
              ) : (
                topDueCustomers.map((customer) => {
                  const status = getCustomerStatus(customer);
                  return (
                    <button
                      type="button"
                      key={getCustomerMobile(customer) || customer.id}
                      className="gsd-customer-row-card"
                      onClick={() => openCustomerDetail(customer)}
                    >
                      <div className="gsd-avatar">{getCustomerInitial(customer)}</div>
                      <div className="gsd-customer-row-main">
                        <strong>{getCustomerName(customer)}</strong>
                        <span>{getCustomerMobile(customer) || '-'}</span>
                      </div>
                      <div className="gsd-customer-row-right">
                        <strong>{formatShortCurrency(getCustomerDue(customer))}</strong>
                        <span className={`gsd-status-pill ${status}`}>{getStatusLabel(status)}</span>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          <div className="gsd-panel-card">
            <div className="gsd-section-title">Due Aging</div>
            <div className="gsd-muted-text">Kitne din se paisa baki hai.</div>

            <div className="gsd-aging-box">
              <div className="gsd-aging-row">
                <span className="dot green"></span>
                <span>0-7 days</span>
                <strong>{formatShortCurrency(dashboardData.dueAging.days0to7)}</strong>
              </div>
              <div className="gsd-aging-row">
                <span className="dot yellow"></span>
                <span>8-30 days</span>
                <strong>{formatShortCurrency(dashboardData.dueAging.days8to30)}</strong>
              </div>
              <div className="gsd-aging-row">
                <span className="dot red"></span>
                <span>30+ days</span>
                <strong>{formatShortCurrency(dashboardData.dueAging.days30plus)}</strong>
              </div>
            </div>

            <div className="gsd-feature-grid">
              <div className="gsd-feature-card">🎙️ Simple Mode</div>
              <div className="gsd-feature-card">🔁 Auto Merge</div>
              <div className="gsd-feature-card">🟢 Paid</div>
              <div className="gsd-feature-card">🔴 Due</div>
            </div>
          </div>
        </div>
      </>
    );
  };

  const renderCustomerList = () => (
    <div className="gsd-customer-page-grid" ref={customerListRef}>
      <div className="gsd-panel-card">
        <div className="gsd-card-header-row">
          <div>
            <div className="gsd-section-title">Customer List</div>
            <div className="gsd-muted-text">Naam ya mobile se turant dhoondhein.</div>
          </div>
          <button type="button" className="gsd-icon-btn" onClick={() => setKhataView('home')}>
            Home
          </button>
        </div>

        <div className="gsd-customer-search-row">
          <input
            type="text"
            className="gsd-search-input"
            placeholder="Search by name or mobile..."
            value={customerSearch}
            onChange={(e) => setCustomerSearch(e.target.value)}
          />

          <select
            className="gsd-filter-select"
            value={customerFilter}
            onChange={(e) => setCustomerFilter(e.target.value)}
          >
            <option value="all">All</option>
            <option value="due">Due</option>
            <option value="partial">Partial</option>
            <option value="paid">Paid</option>
          </select>
        </div>

        <div className="gsd-filter-chip-row">
          {[
            ['all', 'All'],
            ['due', 'Baki Hai'],
            ['partial', 'Adha Paid'],
            ['paid', 'Pura Paid'],
          ].map(([value, label]) => (
            <button
              key={value}
              type="button"
              className={`gsd-chip ${customerFilter === value ? 'active' : ''}`}
              onClick={() => setCustomerFilter(value)}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="gsd-customer-card-list">
          {loading ? (
            <div className="gsd-empty-box">Loading...</div>
          ) : filteredCustomers.length === 0 ? (
            <div className="gsd-empty-box">Customer nahi mila.</div>
          ) : (
            filteredCustomers.map((customer) => {
              const status = getCustomerStatus(customer);
              const active =
                String(getCustomerMobile(selectedCustomer)) === String(getCustomerMobile(customer));

              return (
                <button
                  type="button"
                  key={getCustomerMobile(customer) || customer.id}
                  className={`gsd-customer-row-card ${active ? 'active' : ''}`}
                  onClick={() => openCustomerDetail(customer)}
                >
                  <div className="gsd-avatar">{getCustomerInitial(customer)}</div>
                  <div className="gsd-customer-row-main">
                    <strong>{getCustomerName(customer)}</strong>
                    <span>{getCustomerMobile(customer) || '-'}</span>
                    <small>{customer.customer_type || groceryMode}</small>
                  </div>
                  <div className="gsd-customer-row-right">
                    <strong>{formatShortCurrency(getCustomerDue(customer))}</strong>
                    <span className={`gsd-status-pill ${status}`}>{getStatusLabel(status)}</span>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>

      <div className="gsd-desktop-detail-holder">
        {selectedCustomer ? renderCustomerDetail(true) : null}
      </div>
    </div>
  );

  const renderCustomerDetail = (insideDesktop = false) => {
    if (!selectedCustomer) {
      return (
        <div className="gsd-panel-card">
          <div className="gsd-empty-box">Customer select karein.</div>
        </div>
      );
    }

    const status = getCustomerStatus(selectedCustomer);

    return (
      <div className="gsd-panel-card gsd-customer-detail-panel" ref={customerDetailRef}>
        {!insideDesktop && (
          <button type="button" className="gsd-back-btn" onClick={() => setKhataView('customers')}>
            ← Customer List
          </button>
        )}

        <div className="gsd-detail-header">
          <div className="gsd-detail-profile">
            <div className="gsd-avatar large">{getCustomerInitial(selectedCustomer)}</div>
            <div>
              <h3>{getCustomerName(selectedCustomer)}</h3>
              <p>{getCustomerMobile(selectedCustomer) || '-'}</p>
              <span className={`gsd-status-pill ${status}`}>{getStatusLabel(status)}</span>
            </div>
          </div>

          <div className="gsd-total-due-box">
            <span>Total Due</span>
            <strong>{formatCurrency(totalSelectedDue)}</strong>
            <small>Last: {formatDate(selectedCustomer.last_order_raw || selectedCustomer.last_order_date)}</small>
          </div>
        </div>

        <div className="gsd-profile-stats-grid">
          <div className="gsd-profile-stat-card">
            <span>Total Purchase</span>
            <strong>{formatShortCurrency(selectedCustomer.total_purchase || 0)}</strong>
          </div>
          <div className="gsd-profile-stat-card">
            <span>Total Paid</span>
            <strong className="green-text">{formatShortCurrency(getCustomerPaid(selectedCustomer))}</strong>
          </div>
          <div className="gsd-profile-stat-card due-card">
            <span>Baki</span>
            <strong>{formatShortCurrency(totalSelectedDue)}</strong>
          </div>
          <div className="gsd-profile-stat-card">
            <span>Bills</span>
            <strong>{selectedCustomer.orders || customerInsight.totalOrders || 0}</strong>
          </div>
        </div>

        <div className="gsd-action-grid">
          <button
            type="button"
            className="gsd-pay-action green"
            disabled={totalSelectedDue <= 0}
            onClick={() =>
              openPaymentBox({
                type: 'full',
                amount: totalSelectedDue > 0 ? String(totalSelectedDue) : '',
              })
            }
          >
            <span>₹</span>
            <strong>Full Payment</strong>
          </button>

          <button
            type="button"
            className="gsd-pay-action blue"
            disabled={totalSelectedDue <= 0}
            onClick={() => openPaymentBox({ type: 'partial' })}
          >
            <span>◔</span>
            <strong>Partial Payment</strong>
          </button>

          <button
            type="button"
            className="gsd-pay-action yellow"
            disabled={totalSelectedDue <= 0}
            onClick={() => sendWhatsAppReminder(selectedCustomer)}
          >
            <span>🟢</span>
            <strong>Reminder</strong>
          </button>

          <button
            type="button"
            className="gsd-pay-action purple"
            onClick={() => printCustomerReceipt(selectedCustomer)}
          >
            <span>🖨️</span>
            <strong>Print</strong>
          </button>
        </div>

        {paymentBox.open && (
          <div className="gsd-payment-box">
            <div className="gsd-card-header-row">
              <div>
                <div className="gsd-section-title">
                  {paymentBox.type === 'full' ? 'Full Payment Save' : 'Partial Payment Save'}
                </div>
                <div className="gsd-muted-text">
                  Amount save hote hi customer ka baki hisab update ho jayega.
                </div>
              </div>
              <button type="button" className="gsd-icon-btn" onClick={closePaymentBox}>
                Close
              </button>
            </div>

            <div className="gsd-payment-form-grid">
              <input
                type="number"
                className="gsd-payment-input"
                placeholder="Amount"
                value={paymentBox.amount}
                onChange={(e) => setPaymentBox((prev) => ({ ...prev, amount: e.target.value }))}
              />

              <select
                className="gsd-filter-select"
                value={paymentBox.paymentMode}
                onChange={(e) =>
                  setPaymentBox((prev) => ({ ...prev, paymentMode: e.target.value }))
                }
              >
                <option value="cash">Cash</option>
                <option value="upi">UPI</option>
                <option value="card">Card</option>
                <option value="bank">Bank</option>
                <option value="other">Other</option>
              </select>

              <input
                type="text"
                className="gsd-payment-input"
                placeholder="Reference no. optional"
                value={paymentBox.referenceNo}
                onChange={(e) =>
                  setPaymentBox((prev) => ({ ...prev, referenceNo: e.target.value }))
                }
              />

              <input
                type="text"
                className="gsd-payment-input"
                placeholder="Note optional"
                value={paymentBox.note}
                onChange={(e) => setPaymentBox((prev) => ({ ...prev, note: e.target.value }))}
              />
            </div>

            <button
              type="button"
              className="gsd-save-payment-btn"
              disabled={savingPayment}
              onClick={submitPayment}
            >
              {savingPayment ? 'Saving...' : 'Payment Save Karo'}
            </button>
          </div>
        )}

        <div className="gsd-detail-split">
          <div>
            <div className="gsd-card-header-row">
              <div className="gsd-section-title">Pending Bills</div>
            </div>

            {selectedBills.length === 0 ? (
              <div className="gsd-empty-box">Is customer ka koi pending bill nahi hai.</div>
            ) : (
              <div className="gsd-bill-cards">
                {selectedBills.map((bill) => {
                  const due = Number(bill.due_amount || 0);
                  const billStatus =
                    due <= 0 ? 'paid' : Number(bill.amount_paid || 0) > 0 ? 'partial' : 'due';

                  return (
                    <div className="gsd-bill-card" key={bill.bill_id}>
                      <div className="gsd-bill-card-top">
                        <div>
                          <strong>{bill.bill_no || `Bill #${bill.bill_id}`}</strong>
                          <span>{formatDateTime(bill.created_at)}</span>
                        </div>
                        <span className={`gsd-status-pill ${billStatus}`}>
                          {getStatusLabel(billStatus)}
                        </span>
                      </div>

                      <div className="gsd-bill-money-row">
                        <span>Total: {formatShortCurrency(bill.final_total)}</span>
                        <span>Paid: {formatShortCurrency(bill.amount_paid)}</span>
                        <strong>Due: {formatShortCurrency(bill.due_amount)}</strong>
                      </div>

                      {due > 0 && (
                        <div className="gsd-bill-actions">
                          <button
                            type="button"
                            className="gsd-mini-btn green"
                            onClick={() =>
                              openPaymentBox({
                                type: 'full',
                                bill,
                                amount: String(due),
                              })
                            }
                          >
                            Full
                          </button>
                          <button
                            type="button"
                            className="gsd-mini-btn blue"
                            onClick={() =>
                              openPaymentBox({
                                type: 'partial',
                                bill,
                                amount: '',
                              })
                            }
                          >
                            Partial
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div>
            <div className="gsd-card-header-row">
              <div className="gsd-section-title">Payment History</div>
            </div>

            {customerInsight.paymentTimeline.length === 0 ? (
              <div className="gsd-empty-box">History nahi mila.</div>
            ) : (
              <div className="gsd-timeline-list">
                {customerInsight.paymentTimeline.slice(0, 12).map((item, index) => (
                  <div className="gsd-timeline-item" key={`${item.type}_${index}`}>
                    <div className={`gsd-timeline-dot ${item.type === 'payment' ? 'green' : 'blue'}`}>
                      {item.type === 'payment' ? '₹' : '🧾'}
                    </div>
                    <div>
                      <strong>{item.label || item.type}</strong>
                      <span>{formatDateTime(item.created_at)}</span>
                    </div>
                    <b>{formatShortCurrency(item.amount)}</b>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="gsd-panel-sub-card">
          <div className="gsd-section-title">Recently Bought Products</div>
          {customerInsight.purchasedProducts.length === 0 ? (
            <div className="gsd-empty-box">Product history nahi mila.</div>
          ) : (
            <div className="gsd-product-chip-row">
              {customerInsight.purchasedProducts.slice(0, 12).map((item, index) => (
                <div className="gsd-product-chip" key={`${item.product_name}_${index}`}>
                  <strong>{item.product_name}</strong>
                  <span>
                    Qty {Number(item.quantity || 0)} · {formatShortCurrency(item.total_amount)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderKhataSection = () => (
    <>
      <div className="gsd-khata-top-switch">
        <button
          type="button"
          className={`gsd-khata-top-btn ${khataView === 'home' ? 'active' : ''}`}
          onClick={() => setKhataView('home')}
        >
          Khata Home
        </button>
        <button
          type="button"
          className={`gsd-khata-top-btn ${
            khataView === 'customers' || khataView === 'detail' ? 'active' : ''
          }`}
          onClick={() => openCustomers('all')}
        >
          Customer Hisab
        </button>
      </div>

      {khataView === 'home' && renderKhataHome()}
      {khataView === 'customers' && renderCustomerList()}
      {khataView === 'detail' && renderCustomerDetail(false)}
    </>
  );

  return (
    <>
      <Dashboard />

      <div
        className="page-below-navbar gsd-page-shell"
        onClick={() => {
          if (showReceivedFilter) {
            setShowReceivedFilter(false);
          }
        }}
      >
        <div className="gsd-container">
          <div className="gsd-page-head">
            <div className="gsd-page-head-line" />
            <div>
              <h2>Smart Grocery Dashboard</h2>
              <p>Stock aur Khata Book dono ek jagah</p>
            </div>
            <div className="gsd-page-head-line" />
          </div>

          <div className="gsd-mode-switch-wrap">
            <button
              type="button"
              className={`gsd-main-mode-btn ${activeSection === 'khata' ? 'active' : ''}`}
              onClick={() => setActiveSection('khata')}
            >
              Khata Book
            </button>
            <button
              type="button"
              className={`gsd-main-mode-btn ${activeSection === 'stock' ? 'active' : ''}`}
              onClick={() => setActiveSection('stock')}
            >
              Stock
            </button>

            <select
              className="gsd-mode-select"
              value={groceryMode}
              onChange={(e) => setGroceryMode(e.target.value)}
            >
              <option value="retail">Retail</option>
              <option value="wholesale">Wholesale</option>
            </select>
          </div>

          {message ? <div className="gsd-top-message">{message}</div> : null}

          {activeSection === 'khata' ? renderKhataSection() : renderStockSection()}
        </div>
      </div>
    </>
  );
};

export default GroceryStockDashboard;
